import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { useSocialExchange } from '../hooks/auth-actions.js';
import { useAuthClient } from '../hooks/auth-provider.js';
import { mergeFormClasses } from './shared.js';
import { SOCIAL_LOGIN_PENDING_KEY } from './SocialSignInButtons.js';
/**
 * Drop into the route the SPA passes as `<SocialSignInButtons callbackPath>` (defaults to
 * `/auth/callback`). On mount it:
 *
 *   1) Reads `?code=<returnToCode>` from the current URL.
 *   2) Reads the `pendingSocialLogin` blob (`{initCode, next}`) the buttons stored in
 *      localStorage before redirecting to the IdP.
 *   3) POSTs `/auth/social/exchange { initCode, returnToCode }` and persists the resulting
 *      session token via the same path as a normal login.
 *   4) Calls `onSuccess(next)` with the original next-path so the app can navigate.
 *
 * Renders nothing (or a tiny status panel) while the exchange is in flight. Idempotent: if
 * the localStorage blob is missing or the URL has no `code`, it surfaces a clear error
 * instead of silently retrying.
 */
export function SocialCallback(props) {
    const classes = mergeFormClasses(props.classNames);
    const client = useAuthClient();
    const exchange = useSocialExchange();
    const [state, setState] = React.useState({ status: 'exchanging' });
    // Strict mode + dev double-mount guard. The exchange consumes the (initCode, returnToCode)
    // pair on the server — running twice 404s the second call. A ref keeps the second mount
    // from re-firing.
    const ranRef = React.useRef(false);
    React.useEffect(() => {
        if (ranRef.current)
            return;
        ranRef.current = true;
        const url = new URL(window.location.href);
        const returnToCode = url.searchParams.get('code');
        const flowIdInUrl = url.searchParams.get('flow');
        let pending = null;
        try {
            const raw = window.localStorage.getItem(SOCIAL_LOGIN_PENDING_KEY);
            pending =
                raw === null
                    ? null
                    : JSON.parse(raw);
        }
        catch {
            pending = null;
        }
        // Branch 1 — Kratos came back with `?flow=<id>` (no `?code=`). That means the OIDC
        // result wasn't directly usable: most commonly because the IdP's email matches an
        // existing local identity and Kratos wants the user to confirm ownership (link
        // credentials). Probe the flow and route to the link UI.
        if (returnToCode === null && flowIdInUrl !== null) {
            void client
                .inspectLinkFlow({ flowId: flowIdInUrl })
                .then((inspection) => {
                if (inspection.isLinkCredentialsFlow) {
                    // Clear the pending exchange blob — the link page completes via a separate
                    // `<LinkAccountForm>` flow that returns its own session token; the parked
                    // initCode would only confuse a future SocialCallback mount.
                    try {
                        window.localStorage.removeItem(SOCIAL_LOGIN_PENDING_KEY);
                    }
                    catch {
                        // localStorage might be disabled (private browsing on some platforms); the
                        // worst case is a stale blob that the next click will overwrite.
                    }
                    setState({ status: 'link_required', flowId: flowIdInUrl });
                    if (props.onLinkRequired !== undefined) {
                        props.onLinkRequired(flowIdInUrl);
                    }
                    return;
                }
                // The flow is back without a code AND isn't in the link state — most likely
                // Kratos auto-converted it to a registration flow (no local account exists for
                // this IdP email and we haven't yet wired the "no silent signup" guard). Surface
                // a clear error rather than letting the user think the page hung.
                const error = new Error('The OIDC sign-in returned an unexpected flow state. Try signing in again, or sign up if you don’t have an account.');
                setState({ status: 'error', error });
                props.onFailure?.(error);
            })
                .catch((reason) => {
                const error = reason instanceof Error ? reason : new Error(String(reason));
                setState({ status: 'error', error });
                props.onFailure?.(error);
            });
            return;
        }
        // Branch 2 — normal exchange path: URL has `?code=` and we have the `initCode` parked
        // in localStorage from when the social button was clicked.
        if (returnToCode === null || pending === null || pending.initCode === undefined) {
            const error = new Error(returnToCode === null
                ? 'The OIDC callback URL is missing the `code` query parameter.'
                : 'No pending social-login state was found in this browser. Try signing in again.');
            setState({ status: 'error', error });
            props.onFailure?.(error);
            return;
        }
        // Sanitize the stored `next` — if a previous failed attempt parked an auth-internal
        // path here (e.g. `/auth/callback?code=stale`), navigating to it after this exchange
        // would render the same callback page, see no usable URL params + an empty pending
        // blob, and surface "no pending state". Default to '/' for any auth surface.
        const rawNext = pending.next ?? '/';
        const next = /^\/(login|signup|recovery|verification|mfa|auth)\b/.test(rawNext) ? '/' : rawNext;
        const mode = pending.mode ?? 'login';
        void exchange
            .run({ initCode: pending.initCode, returnToCode, mode })
            .then(() => {
            // Clear the pending blob first so a manual refresh doesn't try to reuse it.
            window.localStorage.removeItem(SOCIAL_LOGIN_PENDING_KEY);
            setState({ status: 'done' });
            props.onSuccess(next);
        })
            .catch((reason) => {
            const error = reason instanceof Error ? reason : new Error(String(reason));
            setState({ status: 'error', error });
            props.onFailure?.(error);
        });
        // exchange.run is stable per useAsyncAction; intentionally only run once on mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    if (props.renderState !== undefined) {
        return _jsx(_Fragment, { children: props.renderState(state) });
    }
    if (state.status === 'exchanging') {
        return (_jsx("div", { style: { display: 'grid', placeItems: 'center', minHeight: '100vh', color: '#666' }, children: "Completing sign-in\u2026" }));
    }
    if (state.status === 'link_required') {
        // Default: stay on this page with a message until the host app navigates via
        // `onLinkRequired`. If onLinkRequired wasn't provided, surface a static link.
        return (_jsx("div", { style: { display: 'grid', placeItems: 'center', minHeight: '100vh', padding: 32 }, children: _jsxs("div", { style: { maxWidth: 480, textAlign: 'center' }, children: [_jsx("h1", { style: { marginBottom: 12 }, children: "Almost done" }), _jsx("p", { className: classes.hint, children: "That email already has an account. Confirm it's yours to link this sign-in method." }), _jsx("a", { className: classes.link, href: `/auth/link-account?flow=${state.flowId}`, children: "Continue" })] }) }));
    }
    if (state.status === 'error') {
        // Host apps that wire `onFailure` typically navigate elsewhere (e.g. /login with the
        // error in the URL) — render a compact "Redirecting…" while that happens so the user
        // doesn't see a full "Sign-in failed" page flash. When `onFailure` isn't wired we still
        // render the message inline so the user isn't stranded with no feedback.
        if (props.onFailure !== undefined) {
            return (_jsx("div", { style: { display: 'grid', placeItems: 'center', minHeight: '100vh', color: '#666' }, children: "Redirecting\u2026" }));
        }
        return (_jsx("div", { style: { display: 'grid', placeItems: 'center', minHeight: '100vh', padding: 32 }, children: _jsxs("div", { style: { maxWidth: 480, textAlign: 'center' }, children: [_jsx("h1", { style: { marginBottom: 12 }, children: "Sign-in failed" }), _jsx("p", { className: classes.error, role: "alert", children: state.error.message })] }) }));
    }
    return (_jsx("div", { style: { display: 'grid', placeItems: 'center', minHeight: '100vh' }, children: "Signed in." }));
}
//# sourceMappingURL=SocialCallback.js.map